import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { isForgeAppsInJSMPhase1Enabled } from 'feature-flags';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { getFabricEmojiUrl } from 'state/selectors/env';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import { PortalCardClient } from 'view/common/smart-card-client';
import { DefaultExtensionProvider, type ExtensionManifest } from '@atlaskit/editor-common/extensions';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { MediaProvider } from '@atlaskit/editor-core';
import { SmartCardProvider } from '@atlaskit/link-provider';
import type { AuthProvider } from '@atlaskit/media-core';
import { xcss } from '@atlaskit/primitives';
import { ReactRenderer } from '@atlaskit/renderer';
import { parseAdf } from '@atlassian/help-center-common-util/adf';
import { useGetValidHelpCenterAuthProvider } from '@atlassian/help-center-common-util/help-center-auth-provider';
import type { editorViewRendererFragment$key } from './__generated__/editorViewRendererFragment.graphql';
import { withEditorExtensions } from './with-editor-extensions';

interface RichTextViewerProps {
    editorExtensions?: Promise<ExtensionManifest[]>;
    editorElement: editorViewRendererFragment$key;
    disableAllLinks?: boolean;
}

const smartCardClient = new PortalCardClient();

const EditorViewerComponent: React.FC<RichTextViewerProps> = ({ editorElement, disableAllLinks, editorExtensions }) => {
    di(ProviderFactory, ReactRenderer, useFragment);
    const data = useFragment(
        graphql`
            fragment editorViewRendererFragment on HelpLayoutEditorElement {
                adf
                elementType @required(action: THROW) {
                    mediaConfig(parentAri: $helpCenterAri) @required(action: THROW) {
                        mediaCollectionName @required(action: THROW)
                        mediaToken @required(action: THROW)
                        mediaUrl @required(action: THROW)
                        asapIssuer @required(action: THROW)
                    }
                }
            }
        `,
        editorElement
    );
    const { adf } = data;

    const { mediaCollectionName, mediaToken, mediaUrl, asapIssuer } = data.elementType.mediaConfig;

    const authProvider: AuthProvider = useGetValidHelpCenterAuthProvider({
        asapIssuer,
        token: mediaToken,
        baseUrl: mediaUrl,
    }); // TODO: get expiry duration via graphql

    const mediaProvider: MediaProvider = React.useMemo(
        () => ({
            uploadParams: {
                collection: mediaCollectionName,
            },
            viewMediaClientConfig: {
                authProvider,
                useSha256ForUploads: true,
            },
        }),
        [authProvider, mediaCollectionName]
    );

    const providerFactory = React.useMemo(() => {
        return ProviderFactory.create({
            mediaProvider: Promise.resolve(mediaProvider),
            extensionProvider: editorExtensions && Promise.resolve(new DefaultExtensionProvider(editorExtensions)),
        });
    }, [editorExtensions, mediaProvider]);

    const [rendererKey, setRendererKey] = useState('without-editor-extensions');

    useEffect(() => {
        async function setExtensionProvider() {
            const extensions = await editorExtensions;
            if (extensions && extensions.length > 0 && providerFactory) {
                providerFactory.removeProvider('extensionProvider');
                providerFactory.setProvider(
                    'extensionProvider',
                    Promise.resolve(new DefaultExtensionProvider(extensions))
                );
            }
        }
        if (editorExtensions) {
            setExtensionProvider()
                .then(() => {
                    setRendererKey('with-editor-extensions');
                })
                .catch(() => {
                    setRendererKey('editor-extension-error');
                });
        }
    }, [editorExtensions, providerFactory]);

    if (typeof adf === 'string')
        return (
            <ElementRoot
                elementWidth={ElementWidth.COMPACT}
                customStyles={disableAllLinks ? [disableRootStyle] : undefined}
            >
                <SmartCardProvider client={smartCardClient}>
                    <ReactRenderer
                        key={rendererKey}
                        appearance={'full-width'}
                        document={parseAdf(adf)}
                        dataProviders={providerFactory}
                        media={{
                            allowLinking: true,
                            allowCaptions: true,
                        }}
                        allowAltTextOnImages
                        shouldOpenMediaViewer
                    />
                </SmartCardProvider>
            </ElementRoot>
        );
    return null;
};

const selector = createSelector(getFabricEmojiUrl, (fabricEmojiUrl) => ({
    fabricEmojiUrl,
}));

const RichTextViewer = isForgeAppsInJSMPhase1Enabled()
    ? withEditorExtensions(EditorViewerComponent)
    : EditorViewerComponent;

export default connect(selector, {})(RichTextViewer);

const disableRootStyle = xcss({
    pointerEvents: 'none',
});
